
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { ROLES, getMemberRoleDisplayName } from 'constants/roles'
import { COMPANY_USER_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'

import { useSelector } from 'store/hooks'
import { getNameOrEmailSlug } from 'utils'

import type { Profile } from 'types'

import { useAuth, useDidMountEffect, useDisclosure, useReferrerState, useWorkOSAuth } from 'hooks'
import { useUsers, useCompanyUsers, useCompany, useCompanySeats, formatSeatUsage, useInviteCompanyAdmins, useSetMemberActive, useRemoveMember  } from 'hooks/api'
import { useSnackbar } from 'contexts'
import { UserPlusIcon, InfoIcon, ArchiveIcon, ArrowUpCircleIcon, UploadIcon, UsersIcon, MailIcon, PencilIcon, RotateCcwIcon, Trash2Icon, ShieldIcon, LockIcon } from 'lucide-react'
import { ROUTES } from 'routes'

import { useTheme, Stack, Typography, Avatar, Link, FormControlLabel } from '@mui/material'

import { Button, Skeleton, Switch } from '@repo/ui'
import { PermissionGate } from 'components/access'
import {
  Header,
  Table,
  Loader,
  EmptyState,
  StatusChipCell,
  InviteCompanyUserModal,
  ImportCompanyUsersModal,
  ActionDropdown,
  UserCell,
  DeleteCompanyUserModal,
  ArchiveCompanyUserModal,
  BulkUserActionModal,
  EditCompanyUserModal,
  AssignRoleModal,
  SignupDomainModal,
  SeatLimitModal,
  InfoTooltip,
} from 'components'
import type { BulkUserAction } from 'components'


export const UsersPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { user: workosUser } = useWorkOSAuth()
  const { open: notification } = useSnackbar()
  // Referrer state handed to every drill-in so the destination Header can offer
  // "← Back to Users".
  const referrer = useReferrerState()

  const [selectedUser, setSelectedUser] = useState() //<Profile | null>(null)
  const [openInviteUserModal, setOpenInviteUserModal] = useState(false)
  const [openEditCompanyUserModal, setOpenEditCompanyUserModal] = useState(false)
  const [openAssignRoleModal, setOpenAssignRoleModal] = useState(false)
  const [openDeleteCompanyUserModal, setOpenDeleteCompanyUserModal] = useState(false)
  const [openArchiveCompanyUserModal, setOpenArchiveCompanyUserModal] = useState(false)
  const [archiveReactivate, setArchiveReactivate] = useState(false)
  const [openImportCompanyUsersModal, setOpenImportCompanyUsersModal] = useState(false)
  const [openSignupDomainModal, setOpenSignupDomainModal] = useState(false)
  // VRG-438 — opened INSTEAD of the invite modal once every seat is in use.
  const [openSeatLimitModal, setOpenSeatLimitModal] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  // Draft value for the Filters panel — committed to showArchived only on Apply.
  const [showArchivedDraft, setShowArchivedDraft] = useState(false)

  // Bulk selection + bulk-action modal state (VERA-816).
  const [rowSelectionModel, setRowSelectionModel] = useState<any[]>([])
  const [bulkAction, setBulkAction] = useState<BulkUserAction | null>(null)
  const [bulkUsers, setBulkUsers] = useState<any[]>([])
  const [isBulkPending, setIsBulkPending] = useState(false)

  // Get company ID from WorkOS user's company
  const companyId = workosUser?.companyId || workosUser?.company?.id || '';

  // Fetch company data (needed for ImportCompanyUsersModal + the Signup domain modal)
  const { data: company } = useCompany(
    { id: companyId },
    { enabled: !!companyId }
  )

  // Fetch company members using the 'me' endpoint
  const {
    data: companyUsersData,
    isLoading,
    refetch,
  } = useCompanyUsers()

  // VRG-438 — seats used vs. seats the plan sells. Read from the API rather than
  // counted off `allUsers` below, because that list is a different population: it
  // includes Vera staff attached to the company, and (with "Show archived" on)
  // archived members, neither of which occupies a seat. Deriving it here would put
  // a number on screen that disagrees with the one the API enforces.
  const { seats, isLoading: seatsLoading, refetch: refetchSeats } = useCompanySeats({
    enabled: !!companyId,
  })

  const allUsers = companyUsersData?.data || []

  // Filter out archived users unless showArchived is true
  const users = useMemo(() => {
    if (showArchived) {
      return allUsers
    }
    return allUsers.filter(user => user.invitation_status !== 'inactive')
  }, [allUsers, showArchived])

  const total = companyUsersData?.total || 0

  // Re-invite mutation
  const { mutate: reinviteUser, isPending: isReinviting } = useInviteCompanyAdmins({
    onSuccess: (data) => {
      notification({
        type: 'success',
        title: `Successfully re-invited user`,
      });
      refetch();
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error.message || 'Failed to re-invite user';
      notification({
        type: 'error',
        title: message,
      });
    },
  });

  // Bulk mutations — looped over the selection in the bulk-action modal.
  const { mutateAsync: setMemberActiveAsync } = useSetMemberActive()
  const { mutateAsync: removeMemberAsync } = useRemoveMember()

  const openBulkModal = (action: BulkUserAction, selectedRows: any[]) => {
    setBulkUsers(selectedRows)
    setBulkAction(action)
  }

  const handleCloseBulkModal = () => {
    setBulkAction(null)
  }

  const handleConfirmBulkAction = async () => {
    if (!bulkAction || bulkUsers.length === 0) return
    const targetCompanyId = company?.id || companyId
    if (bulkAction !== 'delete' && !targetCompanyId) {
      notification({ type: 'error', title: 'Cannot update users: missing company' })
      return
    }

    setIsBulkPending(true)
    try {
      const results = await Promise.allSettled(
        bulkUsers.map((u) =>
          bulkAction === 'delete'
            ? removeMemberAsync({ userId: u.id })
            : setMemberActiveAsync({
                companyId: targetCompanyId,
                userId: u.id,
                active: bulkAction === 'reactivate',
              })
        )
      )

      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.length - succeeded
      const verb = bulkAction === 'delete' ? 'deleted' : bulkAction === 'reactivate' ? 'reactivated' : 'archived'

      if (succeeded > 0) {
        notification({
          type: 'success',
          title: `${succeeded} user${succeeded > 1 ? 's' : ''} ${verb}`,
        })
      }
      if (failed > 0) {
        notification({
          type: 'error',
          title: `${failed} user${failed > 1 ? 's' : ''} could not be ${verb}`,
        })
      }

      setRowSelectionModel([])
      setBulkAction(null)
      refetch()
    } finally {
      setIsBulkPending(false)
    }
  }

  // Bulk-action bar entries, computed from the current selection. When every
  // selected row is already archived we offer Reactivate instead of Archive,
  // mirroring the row-level kebab logic.
  const getBulkActions = useCallback((selectedRows: any[]) => {
    const allArchived =
      selectedRows.length > 0 &&
      selectedRows.every((r) => r.archived || r.invitation_status === 'inactive')

    return [
      allArchived
        ? {
            label: 'Reactivate',
            onClick: () => openBulkModal('reactivate', selectedRows),
            icon: <RotateCcwIcon strokeWidth={1} />,
          }
        : {
            label: 'Archive',
            onClick: () => openBulkModal('archive', selectedRows),
            icon: <ArchiveIcon strokeWidth={1} />,
          },
      {
        label: 'Delete',
        onClick: () => openBulkModal('delete', selectedRows),
        icon: <Trash2Icon strokeWidth={1} />,
        destructive: true,
      },
    ]
  }, [company?.id, companyId])

  /**
   * VRG-438 — at the seat limit, Invite opens the upgrade modal instead.
   *
   * The button stays VISIBLE and ENABLED, which is a deliberate deviation from
   * working-conventions §46 (record state hides the action). §46's own argument is
   * that a tombstone does not help and the app owes the user the RESOLVING action —
   * and here the click DELIVERS that: it names the limit and routes to plans.
   * Hiding Invite would leave a Users page that silently stops letting people be
   * added, with no explanation and no route out.
   *
   * `seats === null` (still loading, or the request failed) falls through to the
   * normal invite. The API is the boundary and will refuse if it must; a UI that
   * cannot read the limit must not invent one.
   */
  const handleInviteUser = () => {
    if (seats && !seats.isUnlimited && (seats.remaining ?? 0) <= 0) {
      setOpenSeatLimitModal(true)
      return
    }
    setOpenInviteUserModal(true)
  }

  const handleViewUser = (row) => {
    navigate(ROUTES.SETTINGS_COMPANY_USER(row.id), { state: referrer })
  }

  const handleEditUser = (row) => {
    navigate(ROUTES.SETTINGS_COMPANY_USER(row.id), { state: referrer })
  }

  const handleCloseEditUserModal = () => {
    setOpenEditCompanyUserModal(false)
  }

  const handleAssignRole = (row) => {
    setSelectedUser(row)
    setOpenAssignRoleModal(true)
  }

  const handleDeleteUser = (row) => {
    setSelectedUser(row)
    setOpenDeleteCompanyUserModal(true)
    console.log(row)
  }

  const handleCloseDeleteUserModal = () => {
    setOpenDeleteCompanyUserModal(false)
  }

  const handleArchiveUser = (row) => {
    setSelectedUser(row)
    setArchiveReactivate(false)
    setOpenArchiveCompanyUserModal(true)
  }

  const handleReactivateUser = (row) => {
    setSelectedUser(row)
    setArchiveReactivate(true)
    setOpenArchiveCompanyUserModal(true)
  }

  const handleCloseArchiveModal = () => {
    setOpenArchiveCompanyUserModal(false)
  }

  const handleReinviteUser = (row) => {
    if (!row?.email) {
      notification({
        type: 'error',
        title: 'Cannot re-invite: user email not found',
      });
      return;
    }

    reinviteUser({
      emails: [row.email],
      roleId: row.role?.id || undefined,
    });
  }


  const handleImportUsers = () => {
    setOpenImportCompanyUsersModal(true)
  }

  const getActionItems = useCallback((row) => {
        const items = [
          {
            label: 'Details',
            onClick: () => handleViewUser(row),
            icon: <InfoIcon strokeWidth={1}/>
          },
        ];

        // Add edit option for active users. The role edit is keyed on the
        // member's user id (resolved to a WorkOS membership server-side), so it
        // no longer requires a workos_membership_id to be present on the row.
        if (row.invitation_status === 'active') {
          items.push({
            label: 'Edit',
            onClick: () => handleEditUser(row),
            icon: <PencilIcon strokeWidth={1}/>
          });
          items.push({
            label: 'Assign role',
            onClick: () => handleAssignRole(row),
            icon: <ShieldIcon strokeWidth={1}/>
          });
        }

        // Add re-invite option for users with 'invited' status
        if (row.invitation_status === 'invited') {
          items.push({
            label: 'Resend Invitation',
            onClick: () => handleReinviteUser(row),
            icon: <MailIcon strokeWidth={1}/>
          });
        }

        // Archived users get Reactivate; everyone else gets Archive (soft).
        const isArchived = row.archived || row.invitation_status === 'inactive';
        if (isArchived) {
          items.push({
            label: 'Reactivate',
            onClick: () => handleReactivateUser(row),
            divider: true,
            icon: <RotateCcwIcon strokeWidth={1}/>
          });
        } else {
          items.push({
            label: 'Archive',
            onClick: () => handleArchiveUser(row),
            divider: true,
            icon: <ArchiveIcon strokeWidth={1}/>
          });
        }

        // Delete (hard, permanent) — always destructive-styled (rule: Delete always red).
        items.push({
          label: 'Delete',
          onClick: () => handleDeleteUser(row),
          destructive: true,
          icon: <Trash2Icon strokeWidth={1}/>
        });

        return items;
    },
    [referrer]
  )

  // Format role name from snake_case to Title Case
  const formatRoleName = useCallback((roleName: string | undefined) => {
    if (!roleName) return '—';
    
    return roleName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, [])


  const columns = useMemo(
    () => [
      {
        field: 'user',
        pinned: 'left',
        headerName: 'User',
        type: 'string',
        disableReorder: true,
        // House default pinned-column width — lines up the right border with
        // the search box above it.
        width: 368,
        renderCell: ({ row }) => (<UserCell user={row} route={ROUTES.SETTINGS_COMPANY_USER(row.id)} />),
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        width: 200,
        renderCell: ({ row }) => {
          const name = getNameOrEmailSlug(row);
          return (
            <Stack
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2">{name || '—'}</Typography>
            </Stack>
          );
        },
      },
      {
        field: 'invitation_status',
        headerName: 'Status',
        type: 'statusChip',
        options: statusFilterOptions(COMPANY_USER_STATUSES),
        width: 190,
        renderCell: ({ row }) => (
          <Stack sx={{ height: '100%', justifyContent: 'center', alignItems: 'flex-start', pl: 2 }}>
            <StatusChipCell statuses={COMPANY_USER_STATUSES} status={row.invitation_status} />
          </Stack>
        ),
      },
      {
        field: 'role',
        headerName: 'Role',
        type: 'string',
        width: 150,
        renderCell: ({ row }) => (
          <Link component={RouterLink} to={ROUTES.SETTINGS_COMPANY_ROLE(row?.role?.name)} state={referrer} underline="hover">
            {getMemberRoleDisplayName(row?.role?.name)}
          </Link>
        ),
      },
      {
        field: 'registeration_date',
        headerName: 'Registration date',
        type: 'string',
        width: 200
      },
      {
        field: 'actions',
        headerName: false,
        renderCell: ({ row }) => <ActionDropdown items={getActionItems(row)} />,
        align: 'center',
        width: 56,
        pinned: 'right',
      },
    ],
    [formatRoleName, getActionItems, referrer]
  )
  
  // as ColumnsType<Profile>[]

  return (
    <>
     <Header
        title="Users"
        // `nested`: this Header sits under the settings page's own "Settings" header, so the
        // wayfinding row it would otherwise reserve is empty space that pushed this title 20px
        // below the non-selfTitled sections' (145.9px vs 125.9px, measured).
        nested
        // total={total}
        rightComponents={
          <Stack direction="row" spacing={4} alignItems="center">
            {/*
              VRG-438 — the seat figure, leftmost in the action group so the buttons keep
              their §26/§33 order (one primary, rightmost; secondaries to its left). It is
              a READOUT, not an action, which is why it sits outside the button cluster
              rather than between two buttons.

              §19: a skeleton at the loaded text's own footprint, not a spinner and not
              nothing. Sized to the widest realistic label ("Unlimited seats"), so the
              adjacent buttons do not shift when the figure arrives.
            */}
            {seatsLoading ? (
              <Skeleton width={104} height={20} />
            ) : (
              seats && (
                <Stack direction="row" alignItems="center">
                  <Typography
                    variant="body3"
                    // Colour when it means something, neutral otherwise.
                    //
                    // NEVER red: the company is grandfathered, keeps every seat, and has done
                    // nothing wrong — error tone would read as a fault to fix. But neutral was
                    // the opposite mistake: "8 of 5" and a healthy "3 of 5" rendered
                    // IDENTICALLY, so the only signal that anything was unusual was the
                    // arithmetic being impossible, and grey actively says "nothing to see".
                    // Warning is the tone for a state that is legitimate but not normal.
                    //
                    // Colour ONLY — same position, same `body3`, same weight. The figure is a
                    // readout, not an alert, so it changes hue and nothing else.
                    //
                    // `isOverLimit`, not `remaining < 0`. `remaining` is `Math.max(limit - used, 0)`
                    // server-side (get-company-seat-usage.use-case.ts:53), so it is FLOORED and
                    // can never be negative — that predicate would be unreachable and this branch
                    // would never fire. `isOverLimit` exists on SeatUsage for exactly this.
                    sx={{
                      color: seats.isOverLimit
                        ? theme.palette.warning.main
                        : theme.palette.text.secondary,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatSeatUsage(seats)}
                  </Typography>
                  {/*
                    `seats-used`, not `seat`. The figure is a COUNT, and the question it
                    provokes is "what is being counted" — which invites are included,
                    whether archived people still cost one — not "what is a seat". The
                    two are separate glossary entries and cross-reference each other, so
                    reaching the other one is one click either way (§44/§59).

                    Reads `short` straight from docs/glossary.json; never a definition
                    retyped at the call site.
                  */}
                  <InfoTooltip term="seats-used" />
                </Stack>
              )
            )}
            {/*
              Upgrade appears ONLY on a limited plan that is AT its limit. Two conditions,
              for two different reasons:

              `!isUnlimited` — on the top tier every plan sells the same (unlimited) seats,
              so an upgrade CTA in a seat context is a dead end: it sends someone to a plan
              page that cannot solve the thing they clicked it about.

              `remaining <= 0` — a PERSISTENT purchase CTA on a page about managing people
              inverts the hierarchy the same way §32's always-on red does. In a four-button
              row it competes with Invite, which is this page's actual primary, and it is the
              loudest thing present on every visit while being relevant on almost none of
              them. The modal already delivers Upgrade at the moment it IS relevant (an
              invite that could not proceed), and Billing stays reachable from the settings
              nav — what is suppressed is the unprompted CTA, not access to plans.

              Deliberately the SAME predicate as `handleInviteUser`'s block below, so the
              button appears exactly when the block is live. Any drift between the two would
              produce the incoherent pair: an Upgrade prompt on a page where Invite still
              works, or a blocked Invite with no upgrade path beside it.
            */}
            {seats && !seats.isUnlimited && (seats.remaining ?? 0) <= 0 && (
              <PermissionGate
                permission="billing_change_plan"
                mode="disable"
                tooltip="You need the “Change plan” permission. Ask an admin to upgrade."
              >
                <Button
                  variant="secondary"
                  disableElevation
                  // §42: hand Billing the referrer so its Header can render
                  // "← Back to Users". This is a LATERAL jump between sibling
                  // surfaces — /settings/users is not an ancestor of
                  // /settings/billing/plan — which is precisely the case §42 keeps
                  // the back-link for, since the breadcrumb there reads
                  // "Settings / Billing / Plan" and cannot name where you came from.
                  // Reuses the page's existing `referrer` rather than calling the
                  // hook a second time.
                  onClick={() => navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })}
                  startIcon={<ArrowUpCircleIcon strokeWidth={1} />}
                >
                  Upgrade
                </Button>
              </PermissionGate>
            )}
             <Button
              variant="secondary"
              disableElevation
              onClick={() => handleImportUsers()}
              startIcon={<UploadIcon strokeWidth={1} />}
            >
              Import
            </Button>
            {/* Secondary, immediately left of the primary Invite button (§26). Opens
                the Signup domain modal, relocated off the page body (VRG-298). */}
            <Button
              variant="secondary"
              disableElevation
              onClick={() => setOpenSignupDomainModal(true)}
              startIcon={<LockIcon strokeWidth={1} />}
            >
              Allowlist
            </Button>
            <Button
              variant="primary"
              disableElevation
              onClick={() => handleInviteUser()}
              startIcon={<UserPlusIcon strokeWidth={1} />}
            >
              Invite
            </Button>
          </Stack>
        }
      />
      {isLoading ? (
        <Loader />
      ) : (
        <>
        
              <Table
                // Full-page table now that the Signup domain panel has moved into
                // its own modal (VRG-298) — fills to a 24px bottom gap (§22).
                fillBottom
                rows={users}
                columns={columns}
                visibilityModel={{ name: false }}
                bulkActions={getBulkActions}
                alwaysShowRowSelection={false}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={setRowSelectionModel}
                extraFilters={
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showArchivedDraft}
                        onChange={(e) => setShowArchivedDraft(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Show archived users</Typography>}
                  />
                }
                extraFiltersDirty={showArchivedDraft !== showArchived}
                onFiltersApply={() => setShowArchived(showArchivedDraft)}
                // Clear owns the extras too — reset the draft AND the applied value,
                // or the next commit bounces the toggle back on.
                onClearExtraFilters={() => {
                  setShowArchivedDraft(false)
                  setShowArchived(false)
                }}
                emptyState={
                   <EmptyState
                title="No users added yet"
                text="Use the button below to invite a new user"
                icon={<UsersIcon strokeWidth={1} size={32}/>}
              //   buttons={
              //     <Button
              //     variant="primary"
              //     disableElevation
              //     onClick={() => handleInviteUser()}
              //     startIcon={<UserPlusIcon strokeWidth={1} />}
              //     >
              //       Invite User
              //     </Button>
              // }
              />    
                }
              />
             <DeleteCompanyUserModal
                user={selectedUser}
                open={openDeleteCompanyUserModal}
                onClose={handleCloseDeleteUserModal}
                refetch={refetch}
              />
              <ArchiveCompanyUserModal
                user={selectedUser}
                open={openArchiveCompanyUserModal}
                onClose={handleCloseArchiveModal}
                refetch={refetch}
                reactivate={archiveReactivate}
              />
              <EditCompanyUserModal
                user={selectedUser}
                open={openEditCompanyUserModal}
                onClose={handleCloseEditUserModal}
                onUpdated={refetch}
              />
              <AssignRoleModal
                user={selectedUser}
                open={openAssignRoleModal}
                onClose={() => setOpenAssignRoleModal(false)}
                refetch={refetch}
              />
              <BulkUserActionModal
                users={bulkUsers}
                action={bulkAction ?? 'archive'}
                open={bulkAction !== null}
                onClose={handleCloseBulkModal}
                onConfirm={handleConfirmBulkAction}
                isLoading={isBulkPending}
              />
        </>
      )}
    <InviteCompanyUserModal
      open={openInviteUserModal}
      onClose={() => setOpenInviteUserModal(false)}
      refetch={() => {
        refetch()
        // A completed invite consumes a seat immediately (a pending invite holds
        // one), so the header figure has to move with the list. Without this the
        // page shows 4 of 5 while the API already counts 5.
        refetchSeats()
      }}
    />
    {/* VRG-438 — opened by handleInviteUser INSTEAD of the invite modal at the limit. */}
    <SeatLimitModal
      open={openSeatLimitModal}
      onClose={() => setOpenSeatLimitModal(false)}
      seats={seats}
    />
    <ImportCompanyUsersModal
      company={company}
      open={openImportCompanyUsersModal}
      onClose={() => setOpenImportCompanyUsersModal(false)}
      refetch={refetch}
    />
    <SignupDomainModal
      company={company}
      open={openSignupDomainModal}
      onClose={() => setOpenSignupDomainModal(false)}
    />
    </>
  )
}
